import { getJwtFromCookie } from '../../../cookies';
import { axiosInstance } from '../../axios';
import {
  Chat,
  ChatDetail,
  ChatMessage,
  ChatShareLink,
  ChatStreamEvent,
  ReasoningEffort,
  SharedChatAccess,
} from './chat.interface';

export function fetchChats() {
  return axiosInstance
    .get<Chat[]>('/api/chat')
    .then((response) => response.data);
}

export function createChat() {
  return axiosInstance.post<Chat>('/api/chat').then((response) => response.data);
}

export function deleteChat(id: string) {
  return axiosInstance
    .delete<Chat>(`/api/chat/${id}`)
    .then((response) => response.data);
}

export function deleteAllChats() {
  return axiosInstance
    .delete<{ deleted: number }>('/api/chat')
    .then((response) => response.data);
}

export function fetchChat(id: string) {
  return axiosInstance
    .get<ChatDetail>(`/api/chat/${id}`)
    .then((response) => response.data);
}

export function createChatMessage(chatId: string, message: string) {
  return axiosInstance
    .post<ChatMessage>(`/api/chat/${chatId}/message`, { message })
    .then((response) => response.data);
}

export function fetchSharedChats() {
  return axiosInstance
    .get<SharedChatAccess[]>('/api/shared-chat')
    .then((response) => response.data);
}

export function revokeSharedChat(id: string) {
  return axiosInstance
    .delete<{ id: string }>(`/api/shared-chat/${id}`)
    .then((response) => response.data);
}

export function shareChat(chatId: string, seconds: number | null) {
  return axiosInstance
    .post<ChatShareLink>(`/api/shared-chat/${chatId}/share`, { seconds })
    .then((response) => response.data);
}

export function fetchSharedChat(token: string) {
  return axiosInstance
    .get<ChatDetail>(`/api/shared-chat/${encodeURIComponent(token)}`)
    .then((response) => response.data);
}

export function downloadSharedChatFile(token: string, fileId: string) {
  return axiosInstance
    .get<Blob>(
      `/api/shared-chat/${encodeURIComponent(token)}/files/${fileId}`,
      { responseType: 'blob' },
    )
    .then((response) => response.data);
}

export function setChatPreset(chatId: string, presetId: string | null) {
  return axiosInstance
    .patch<Chat>(`/api/chat/${chatId}/preset`, { presetId })
    .then((response) => response.data);
}

export function setToolApproval(
  chatId: string,
  messageId: string,
  approved: boolean,
) {
  return axiosInstance
    .post<ChatMessage[]>(`/api/chat/${chatId}/message/${messageId}/approval`, {
      approved,
    })
    .then((response) => response.data);
}

export async function streamChat(
  chatId: string,
  onEvent: (event: ChatStreamEvent) => void,
  signal?: AbortSignal,
  reasoningEffort?: ReasoningEffort,
) {
  const token = getJwtFromCookie();
  const baseURL = axiosInstance.defaults.baseURL ?? '';
  const response = await fetch(`${baseURL}/api/chat/${chatId}/stream`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'text/event-stream',
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      reasoningEffort,
    }),
    signal,
  });

  if (!response.ok || !response.body) {
    throw new Error('Failed to stream chat message');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();

    if (done) {
      break;
    }

    buffer += decoder.decode(value, { stream: true });
    const chunks = buffer.split('\n\n');
    buffer = chunks.pop() ?? '';

    for (const chunk of chunks) {
      const event = parseSseChunk(chunk);

      if (event) {
        onEvent(event);
      }
    }
  }
}

function parseSseChunk(chunk: string): ChatStreamEvent | null {
  let type = '';
  const dataLines: string[] = [];

  for (const line of chunk.split('\n')) {
    if (line.startsWith('event:')) {
      type = line.slice('event:'.length).trim();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice('data:'.length).trimStart());
    }
  }

  if (!type || dataLines.length === 0) {
    return null;
  }

  return {
    type,
    data: JSON.parse(dataLines.join('\n')),
  } as ChatStreamEvent;
}
